import { useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Label } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  FormDialogCancel,
} from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";

type DeleteDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isDisabled: boolean;
  onDelete: () => Promise<void>;
  entityName: string;
  isDeleteLoading: boolean;
  customTrigger?: boolean;
  extraDescription?: React.ReactNode;
  /** Phrase the user must type to confirm. Defaults to `entityName`. */
  confirmationPhrase?: string;
  /** Dialog title. Defaults to `Slet {entityName}`. */
  dialogTitle?: string;
  /** Confirm button label. Defaults to `Slet {entityName}`. */
  confirmButtonText?: string;
};

const DeleteDialog = ({
  isOpen,
  setIsOpen,
  isDisabled,
  onDelete,
  entityName,
  isDeleteLoading,
  customTrigger,
  extraDescription,
  confirmationPhrase,
  dialogTitle,
  confirmButtonText,
}: DeleteDialogProps) => {
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const phraseToConfirm = confirmationPhrase ?? entityName;
  const isConfirmed = deleteConfirmation.trim() === phraseToConfirm.trim();
  const title = dialogTitle ?? `Slet ${entityName}`;
  const deleteLabel = confirmButtonText ?? `Slet ${entityName}`;

  const handleDelete = async () => {
    await onDelete();
    setDeleteConfirmation("");
    // Parent may already have closed the dialog (e.g. bulk delete after UI reset).
    if (isOpen) {
      setIsOpen(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setDeleteConfirmation("");
    }
    setIsOpen(open);
  };

  return (
    <>
      {!customTrigger && (
        <Button
          variant="destructive"
          disabled={isDisabled}
          onClick={() => setIsOpen(true)}
        >
          <Trash2 className="mr-2 size-4" />
          Slet {entityName}
        </Button>
      )}
      <FormDialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        isDirty={deleteConfirmation.trim() !== ""}
        onKeyDown={(e) => {
          if (e.key === "Enter" && isConfirmed && !isDisabled) {
            e.preventDefault();
            handleDelete();
          }
        }}
      >
        <DialogHeader className="space-y-3">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-destructive/10 ">
            <AlertTriangle className="size-6 text-destructive " />
          </div>
          <DialogTitle className="text-center text-xl font-semibold">
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="text-center text-sm text-muted-foreground">
            Denne handling kan ikke fortrydes. Dette vil permanent slette
            <span className="font-semibold text-foreground">
              {" "}
              {entityName}{" "}
            </span>
            og fjerne alt dets indhold fra vores servere.
          </div>

          {extraDescription}

          <div className="space-y-3">
            <Label
              htmlFor="delete-confirmation"
              className="font-normal text-sm inline-block"
            >
              For at bekræfte, skriv{" "}
              <span className="font-semibold text-foreground italic">
                {phraseToConfirm}
              </span>{" "}
              i boksen nedenfor:
            </Label>
            <Input
              id="delete-confirmation"
              type="text"
              className={cn(
                "transition-all duration-200",
                isConfirmed && "border-destructive/30 "
              )}
              placeholder={`Udfyld med: ${phraseToConfirm}`}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              value={deleteConfirmation}
              autoComplete="off"
            />

            {isConfirmed && (
              <div className="text-xs text-destructive animate-in fade-in slide-in-from-top-1 duration-200">
                Advarsel: Denne handling kan ikke fortrydes
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="grid grid-cols-2">
          <FormDialogCancel />
          <Button
            variant="destructive"
            disabled={!isConfirmed || isDeleteLoading}
            onClick={handleDelete}
          >
            {isDeleteLoading ? (
              <Spinner className="mr-2" />
            ) : (
              <Trash2 className="mr-2 size-4" />
            )}
            {deleteLabel}
          </Button>
        </DialogFooter>
      </FormDialog>
    </>
  );
};
export default DeleteDialog;
